/**
 * src/components/shared/ReleaseFlagGate.jsx
 *
 * Route guard for a release-flagged module (RPDA-04). Renders `children` only
 * when the server resolved `flag` ON for the signed-in tenant
 * (useReleaseFlagState → release-flags-read). Fail closed: nothing while the
 * read is in flight, a neutral notice when it is off — worded so it is true
 * both for a module not yet released and for one switched off — and a
 * could-not-check notice when the read failed or came back incomplete (RFLG-B-006 keeps the latter from hiding records a tenant
 * must act on).
 *
 * Visibility only — the module's data is kept and still counted everywhere
 * (RFLG-B-003). Place it inside the plan gate, never instead of it: a release
 * never grants what the plan denies (RFLG-B-004). The module's own endpoints
 * enforce the same answer server-side with checkReleaseFlag().
 */

import { useReleaseFlagState } from '../../utils/useReleaseFlag.js';

export default function ReleaseFlagGate({ flag, children }) {
  const { loaded, released, failed } = useReleaseFlagState();
  if (!loaded) return null;
  if (!released.has(flag)) {
    // A failed read is unknown, never "switched off".
    return (
      <div role="status" style={{ padding: 24, color: 'var(--fg2)' }}>
        {failed
          ? 'We could not check whether this module is available for your organisation. Reload the page to try again.'
          : 'This module is not switched on for your organisation.'}
      </div>
    );
  }
  return children;
}
